import { useLayoutEffect, useRef, type RefObject } from 'react';
import { squareName, type MatchEvent, type Side } from '../engine';

/*
 * Cards travel between the deck, the hand and the pile. After each action the match screen hands
 * its events here; every card that moved gets a flight: a copy of the card animated from where
 * it was to where it now lies, while the real card waits hidden until the copy lands (the FLIP
 * technique, with the Web Animations API). Cards leaving the hand fly from where they were drawn
 * on screen just before the action, so the hand's cards are measured after every render.
 */

interface Seen {
  /** Center of the card in page coordinates, and its size. */
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
  readonly rot: number;
  /** The card element, kept after React removes it from the page. */
  readonly el: HTMLElement;
}

interface Flight {
  readonly from: { x: number; y: number; w: number; rot: number };
  /** Where the card lands; hidden until it does. Null: the card flies off and fades out. */
  readonly to: HTMLElement | null;
  /** The card to show in flight (a copy is made); none flies a card back. */
  readonly face: HTMLElement | null;
  /** Starts face down and turns over on the way. */
  readonly reveal: boolean;
  readonly delay: number;
  /** Rotation of the card where it lands, in degrees. */
  readonly rot: number;
  /** A Brazier: the card flies to it and goes up in flames there instead of landing. */
  readonly burnAt?: HTMLElement;
  /** A burn taken back: the card comes up out of the flames at `from` and flies to `to`. */
  readonly unburn?: boolean;
}

const DURATION = 580;
const STAGGER = 95;
const BACK_HTML = '<span class="card-back-art"><span class="card-back-emblem"></span></span>';
const STATE_CLASSES = ['card-playable', 'card-unplayable', 'card-selected'];

function rotationOf(el: Element | null): number {
  const li = el?.closest<HTMLElement>('[data-card-id], [data-pile-top]');
  const value = li?.style.getPropertyValue('--rot') || li?.style.transform || '';
  const m = /(-?[\d.]+)deg/.exec(value);
  return m ? Number(m[1]) : 0;
}

function measure(el: HTMLElement): { x: number; y: number; w: number; h: number } {
  const r = el.getBoundingClientRect();
  return {
    x: r.left + r.width / 2 + window.scrollX,
    y: r.top + r.height / 2 + window.scrollY,
    w: el.offsetWidth || r.width,
    h: el.offsetHeight || r.height,
  };
}

/** A copy of a card for the flight, without its state, focus or keyboard hints. */
function copyCard(card: HTMLElement, sizeFrom?: HTMLElement | null): HTMLElement {
  const copy = card.cloneNode(true) as HTMLElement;
  copy.removeAttribute('id');
  copy.setAttribute('tabindex', '-1');
  copy.classList.remove(...STATE_CLASSES);
  copy.classList.add('card-neutral');
  copy.querySelectorAll('.card-key, .card-ribbon').forEach((n) => n.remove());
  if (sizeFrom)
    for (const c of ['card-xs', 'card-sm', 'card-md', 'card-lg']) {
      copy.classList.toggle(c, sizeFrom.classList.contains(c));
    }
  return copy;
}

/** Where a flight lands, measured before any flight is added to the page (see useCardFlights). */
interface Planned {
  readonly f: Flight;
  readonly land: { x: number; y: number; w: number; h: number } | null;
  /** The page's scroll position then. */
  readonly sx: number;
  readonly sy: number;
}

function plan(f: Flight): Planned {
  const at = f.to ?? f.burnAt;
  return { f, land: at ? measure(at) : null, sx: window.scrollX, sy: window.scrollY };
}

function launch(layer: HTMLElement, { f, land, sx, sy }: Planned, onDone: () => void): Animation[] {
  const target = f.to;
  const w = f.burnAt ? f.from.w : (land?.w ?? f.from.w);
  const h = f.burnAt ? f.from.w * 1.4 : (land?.h ?? f.from.w * 1.4);
  const x = (land?.x ?? f.from.x) - sx;
  const y = (land?.y ?? f.from.y - 70) - sy;
  const el = document.createElement('div');
  el.className = 'flight';
  el.style.left = `${x - w / 2}px`;
  el.style.top = `${y - h / 2}px`;
  el.style.width = `${w}px`;
  el.style.height = `${h}px`;
  const inner = document.createElement('div');
  inner.className = 'flight-inner';
  const face = f.face ? copyCard(f.face, target) : null;
  if (face) {
    face.classList.add('flight-face');
    inner.append(face);
  }
  if (f.reveal || !face) {
    const back = document.createElement('span');
    back.className = `card card-back ${face ? 'flight-back' : ''} ${
      (target ?? f.face)?.className.match(/card-(xs|sm|md|lg)/)?.[0] ?? 'card-md'
    }`;
    back.innerHTML = BACK_HTML;
    inner.append(back);
  }
  el.append(inner);
  layer.append(el);
  // Several flights can land on the same card (the pile's top): it shows when the last lands.
  if (target) target.dataset.flying = String(Number(target.dataset.flying ?? 0) + 1);

  const dx = f.from.x - sx - x;
  const dy = f.from.y - sy - y;
  const s = f.from.w / w;
  const lift = Math.min(90, 26 + Math.hypot(dx, dy) * 0.12);
  const timing: KeyframeAnimationOptions = {
    duration: DURATION,
    delay: f.delay,
    easing: 'cubic-bezier(0.45, 0.05, 0.2, 1)',
    fill: 'both',
  };
  // Cards thrown onto the pile spin a little on the way; every card lands with a small bounce.
  const spin = target?.closest('[data-pile-top]') ? (dx > 0 ? -1 : 1) * 16 : 0;
  const path = f.unburn
    ? [
        {
          transform: `translate(${dx}px, ${dy - 14}px) rotate(12deg) scale(0.25)`,
          filter: 'sepia(1) saturate(6) hue-rotate(-30deg) brightness(2)',
          opacity: 0,
        },
        {
          transform: `translate(${dx}px, ${dy}px) rotate(6deg) scale(0.55)`,
          filter: 'sepia(1) saturate(5) hue-rotate(-25deg) brightness(1.5)',
          opacity: 1,
          offset: 0.24,
        },
        {
          transform: `translate(${dx * 0.45}px, ${dy * 0.45 - lift}px) rotate(-8deg) scale(0.9)`,
          filter: 'none',
          offset: 0.55,
        },
        { transform: `translate(0, 0) rotate(${f.rot}deg) scale(1.07)`, offset: 0.86 },
        { transform: `translate(0, 0) rotate(${f.rot}deg) scale(1)` },
      ]
    : f.burnAt
      ? [
          {
            transform: `translate(${dx}px, ${dy}px) rotate(${f.from.rot}deg) scale(1)`,
            filter: 'none',
          },
          {
            transform: `translate(${dx * 0.45}px, ${dy * 0.45 - lift}px) rotate(-8deg) scale(0.8)`,
            filter: 'none',
            offset: 0.5,
          },
          {
            transform: 'translate(0, 0) rotate(6deg) scale(0.55)',
            filter: 'sepia(1) saturate(5) hue-rotate(-25deg) brightness(1.5)',
            opacity: 1,
            offset: 0.78,
          },
          {
            transform: 'translate(0, -14px) rotate(12deg) scale(0.25)',
            filter: 'sepia(1) saturate(6) hue-rotate(-30deg) brightness(2)',
            opacity: 0,
          },
        ]
      : target
        ? [
            { transform: `translate(${dx}px, ${dy}px) rotate(${f.from.rot}deg) scale(${s})` },
            {
              transform: `translate(${dx * 0.42}px, ${dy * 0.42 - lift}px) rotate(${(f.from.rot + f.rot) / 2 + spin}deg) scale(${(s + 1) / 2 + 0.12})`,
              offset: 0.5,
            },
            { transform: `translate(0, 0) rotate(${f.rot}deg) scale(1.07)`, offset: 0.86 },
            { transform: `translate(0, 0) rotate(${f.rot}deg) scale(1)` },
          ]
        : [
            { transform: `translate(${dx}px, ${dy}px) scale(${s})`, opacity: 1 },
            {
              transform: `translate(${dx}px, ${dy - 40}px) scale(${s * 0.9})`,
              opacity: 0.9,
              offset: 0.4,
            },
            {
              transform: `translate(${dx}px, ${dy - 110}px) scale(${s * 0.6}) rotate(12deg)`,
              opacity: 0,
            },
          ];
  const animations = [el.animate(path, timing)];
  if (f.reveal && face)
    animations.push(
      inner.animate(
        [
          { transform: 'rotateY(180deg)' },
          { transform: 'rotateY(180deg)', offset: 0.25 },
          { transform: 'rotateY(0deg)' },
        ],
        timing,
      ),
    );
  let finished = false;
  const finish = () => {
    if (finished) return;
    finished = true;
    el.remove();
    if (target) {
      const left = Number(target.dataset.flying ?? 1) - 1;
      if (left > 0) target.dataset.flying = String(left);
      else {
        delete target.dataset.flying;
        landed(target);
      }
    }
    onDone();
  };
  void animations[0]!.finished.then(finish, finish);
  return animations;
}

/** Plays a CSS animation class once more on an element, removing it when it ends. */
function replay(el: HTMLElement, className: string): void {
  el.classList.remove(className);
  void el.offsetWidth;
  el.classList.add(className);
  el.addEventListener('animationend', () => el.classList.remove(className), { once: true });
}

/** A card has landed: on the pile it hits hard and shakes the table; in the hand it pops. */
function landed(target: HTMLElement): void {
  const top = target.closest<HTMLElement>('[data-pile-top]');
  if (top) {
    replay(top, 'is-landed');
    const table = top.closest<HTMLElement>('.table-cards');
    if (table) replay(table, 'is-shaken');
    return;
  }
  if (target.closest('[data-card-id]'))
    target.animate([{ scale: '1.14' }, { scale: '1' }], {
      duration: 320,
      easing: 'cubic-bezier(0.34, 1.56, 0.64, 1)',
    });
}

/**
 * Plays the flights for each new step of what the screen shows: `step` changes with every action,
 * played or drafted, and `events` are that step's events. `reverse` plays them backwards (a
 * drafted move taken back: its card flies from the pile to the hand). `side` is the side whose
 * hand is shown (null: none). Also deals the opening hand once, when `deal` is set at first.
 */
export function useCardFlights({
  root,
  layer,
  step,
  events,
  reverse = false,
  side,
  enabled,
  deal,
  onSound,
}: {
  root: RefObject<HTMLElement | null>;
  layer: RefObject<HTMLElement | null>;
  step: string | number;
  events: readonly MatchEvent[];
  reverse?: boolean;
  side: Side | null;
  enabled: boolean;
  deal: boolean;
  onSound?: (name: 'draw' | 'place' | 'fan') => void;
}): void {
  const seen = useRef(new Map<string, Seen>());
  const lastStep = useRef<string | number | null>(null);
  const running = useRef<Animation[]>([]);
  const timers = useRef<number[]>([]);

  useLayoutEffect(() => {
    const rootEl = root.current;
    const layerEl = layer.current;
    const first = lastStep.current === null;
    const changed = !first && lastStep.current !== step;
    lastStep.current = step;
    // Everything is measured first, then the flights are added to the page: measuring between
    // two additions would make the browser lay the page out again for each flight.
    let planned: Planned[] = [];
    if (rootEl && layerEl && enabled && (changed || (first && deal))) {
      const flights = first
        ? dealFlights(rootEl, side)
        : reverse
          ? takeBackFlights(rootEl, events, side)
          : flightsFor(rootEl, events, side, seen.current);
      planned = flights.map(plan);
    }
    // Remember where the hand's cards are now: the next action's cards leave from here.
    const next = new Map<string, Seen>();
    rootEl?.querySelectorAll<HTMLElement>('[data-card-id]').forEach((li) => {
      const card = li.querySelector<HTMLElement>('.card');
      if (!card || !li.dataset.cardId) return;
      next.set(li.dataset.cardId, { ...measure(card), rot: rotationOf(card), el: card });
    });
    seen.current = next;
    if (layerEl && planned.length > 0) {
      const flights = planned.map((p) => p.f);
      // Flights of earlier actions play on: a quick opponent doesn't cut the deal short.
      running.current = running.current.filter((a) => a.playState !== 'finished');
      for (const p of planned) running.current.push(...launch(layerEl, p, () => undefined));
      // The deal sounds as one fan of cards; other cards as they leave the deck or reach the pile.
      if (onSound && first && flights.length > 0) onSound('fan');
      else if (onSound)
        for (const f of flights) {
          if (!f.to) continue;
          const sound = f.to.closest('[data-pile-top]') ? 'place' : 'draw';
          timers.current.push(window.setTimeout(() => onSound(sound), f.delay));
        }
    }
  });

  useLayoutEffect(
    () => () => {
      for (const a of running.current.splice(0)) a.finish();
      for (const id of timers.current.splice(0)) clearTimeout(id);
    },
    [],
  );
}

/**
 * A drafted link taken back: its card leaves the pile and settles back in the hand, or, burned on
 * a Brazier, comes back up out of its flames.
 */
function takeBackFlights(
  root: HTMLElement,
  events: readonly MatchEvent[],
  side: Side | null,
): Flight[] {
  const pile = from(root.querySelector('[data-pile]'));
  if (!pile || !side) return [];
  const out: Flight[] = [];
  for (const e of events) {
    if (e.type !== 'LinkPlayed' || e.side !== side) continue;
    const to = handCard(root, e.cardId);
    if (!to) continue;
    const burned = events.find((x) => x.type === 'CardBurned' && x.cardId === e.cardId);
    const brazier =
      burned?.type === 'CardBurned'
        ? from(root.querySelector(`[data-square="${squareName(burned.square)}"]`))
        : null;
    if (brazier) {
      out.push({
        from: { ...brazier, w: to.offsetWidth || brazier.w },
        to,
        face: to,
        reveal: false,
        delay: 0,
        rot: rotationOf(to),
        unburn: true,
      });
      continue;
    }
    out.push({ from: pile, to, face: to, reveal: false, delay: 0, rot: rotationOf(to) });
  }
  return out;
}

function handCard(root: HTMLElement, cardId: string): HTMLElement | null {
  return root.querySelector<HTMLElement>(`[data-card-id="${CSS.escape(cardId)}"] .card`);
}

function from(el: Element | null): Flight['from'] | null {
  if (!(el instanceof HTMLElement)) return null;
  const m = measure(el);
  return { x: m.x, y: m.y, w: m.w, rot: 0 };
}

/** The opening hand, dealt from the deck one card at a time. */
function dealFlights(root: HTMLElement, side: Side | null): Flight[] {
  if (!side) return [];
  const deck = from(root.querySelector(`[data-deck="${side}"] .deck-stack, [data-deck="${side}"]`));
  if (!deck) return [];
  return [...root.querySelectorAll<HTMLElement>('[data-card-id] .card')].map((card, i) => ({
    from: deck,
    to: card,
    face: card,
    reveal: true,
    delay: 120 + i * STAGGER,
    rot: rotationOf(card),
  }));
}

function flightsFor(
  root: HTMLElement,
  events: readonly MatchEvent[],
  side: Side | null,
  seen: ReadonlyMap<string, Seen>,
): Flight[] {
  const out: Flight[] = [];
  let delay = 0;
  const pileTop = root.querySelector<HTMLElement>('[data-pile-top] .card');
  const strip = (s: Side, what: 'hand' | 'deck') =>
    root.querySelector(`[data-strip-${what}="${s}"]`) ?? root.querySelector(`[data-strip="${s}"]`);
  for (const e of events) {
    switch (e.type) {
      case 'CardDrawn': {
        if (e.reason === 'setup') break;
        if (e.side === side && e.cardId) {
          const to = handCard(root, e.cardId);
          const start = from(
            root.querySelector(`[data-deck="${side}"] .deck-stack, [data-deck="${side}"]`),
          );
          if (to && start)
            out.push({ from: start, to, face: to, reveal: true, delay, rot: rotationOf(to) });
        } else {
          const to = root.querySelector<HTMLElement>(`[data-strip-hand="${e.side}"] .mini-card`);
          const start = from(strip(e.side, 'deck'));
          if (to && start) out.push({ from: start, to, face: null, reveal: false, delay, rot: 0 });
        }
        delay += STAGGER;
        break;
      }
      case 'LinkPlayed': {
        const mine = e.side === side ? seen.get(e.cardId) : undefined;
        const start = mine
          ? { x: mine.x, y: mine.y, w: mine.w, rot: mine.rot }
          : from(strip(e.side, 'hand'));
        // Burned on a Brazier: the card goes into its flames, not onto the pile.
        const burned = events.find((x) => x.type === 'CardBurned' && x.cardId === e.cardId);
        const brazier =
          burned?.type === 'CardBurned'
            ? root.querySelector<HTMLElement>(`[data-square="${squareName(burned.square)}"]`)
            : null;
        if (brazier && start) {
          out.push({
            from: start,
            to: null,
            burnAt: brazier,
            face: mine?.el ?? null,
            reveal: false,
            delay,
            rot: 0,
          });
          delay += STAGGER * 2;
          break;
        }
        if (!pileTop || !start) break;
        out.push({
          from: start,
          to: pileTop,
          face: mine?.el ?? pileTop,
          reveal: !mine,
          delay,
          rot: rotationOf(pileTop),
        });
        delay += STAGGER * 2;
        break;
      }
      case 'EpitaphPlayed': {
        if (!pileTop) break;
        const start = from(
          root.querySelector(`[data-strip="${e.side}"] .strip-epitaph`) ?? strip(e.side, 'hand'),
        );
        if (start)
          out.push({
            from: start,
            to: pileTop,
            face: pileTop,
            reveal: false,
            delay,
            rot: rotationOf(pileTop),
          });
        delay += STAGGER * 2;
        break;
      }
      case 'Recovered': {
        const mine = e.side === side ? seen.get(e.cardId) : undefined;
        if (mine) {
          out.push({
            from: { x: mine.x, y: mine.y, w: mine.w, rot: mine.rot },
            to: null,
            face: mine.el,
            reveal: false,
            delay,
            rot: 0,
          });
          delay += STAGGER * 2;
        }
        break;
      }
      default:
        break;
    }
  }
  return out;
}
